import dayjs from 'dayjs';
import { CountryCode, isValidNumber } from 'libphonenumber-js';
import { z } from '@betty/toolkit/api';

const namesValidationRegex = /^[-.'A-Za-zÀ-ÖØ-öø-ÿ\s]+$/;
export const forbiddenAddressRegex = /^[^~?|+:*^<>=()[\]{}\\]+$/;
export const postalCodeRegex =
  /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

export const RegistrationLimitTypeSchema = z
  .object({
    Daily: z.number().positive().optional(),
    Weekly: z.number().positive().optional(),
    Monthly: z.number().positive().optional(),
  })
  .refine(({ Daily, Weekly }) => (Daily && Weekly ? Daily <= Weekly : true), {
    message: 'Daily amount cannot be more than weekly amount.',
    path: ['Weekly'],
  })
  .refine(
    ({ Daily, Monthly }) => (Daily && Monthly ? Daily <= Monthly : true),
    {
      message: 'Daily amount cannot be more than monthly amount.',
      path: ['Monthly'],
    },
  )
  .refine(
    ({ Weekly, Monthly }) => (Weekly && Monthly ? Weekly <= Monthly : true),
    {
      message: 'Weekly amount cannot be more than monthly amount.',
      path: ['Monthly'],
    },
  );

export const RegistrationLimitSchema = z.object({
  Deposit: RegistrationLimitTypeSchema,
  Loss: RegistrationLimitTypeSchema,
});

export const MarketingPreferencesSchema = z.object({
  allowEmailMarketingTool: z.boolean(),
  allowSMSMarketingTool: z.boolean(),
});

export const MarketingMetaSchema = z.object({
  promotion_id: z.number().optional(),
  gclid: z.string().nullable().optional(),
  adfcd: z.string().nullable().optional(),
  fbclid: z.string().nullable().optional(),
  msclkid: z.string().nullable().optional(),
  utm_source: z.string().nullable().optional(),
  utm_medium: z.string().nullable().optional(),
  utm_campaign: z.string().nullable().optional(),
  utm_content: z.string().nullable().optional(),
  utm_term: z.string().nullable().optional(),
  utm_variant: z.string().nullable().optional(),
  referral_code: z.string().nullable().optional(),
  referrer_player_id: z.number().nullable().optional(),
  btag: z.string().nullable().optional(),
  s1: z.string().nullable().optional(),
  t1: z.string().nullable().optional(),
  lpageid: z.string().nullable().optional(),
  ad_group: z.string().nullable().optional(),
  adset: z.string().nullable().optional(),
  creative: z.string().nullable().optional(),
  keyword: z.string().nullable().optional(),
  network: z.string().nullable().optional(),
  wbraid: z.string().nullable().optional(),
  gbraid: z.string().nullable().optional(),
  ttclid: z.string().nullable().optional(),
  dclid: z.string().nullable().optional(),
  location: z.string().nullable().optional(),
  device_id: z.string().nullable().optional(),
  click_date: z.string().nullable().optional(),
});

export const PersonalDetailsSchema = z.object({
  firstName: z
    .string()
    .min(1, { message: 'Please enter your first name' })
    .refine((val) => namesValidationRegex.test(val) || val.length === 0, {
      message:
        "Invalid characters detected. Only letters, spaces, apostrophes ('), periods (.), and hyphens (-) are allowed.'",
    }),
  middleName: z
    .string()
    .refine((val) => namesValidationRegex.test(val) || val.length === 0, {
      message:
        "Invalid characters detected. Only letters, spaces, apostrophes ('), periods (.), and hyphens (-) are allowed.'",
    }),
  lastName: z
    .string()
    .min(1, { message: 'Please enter your last name' })
    .refine((val) => namesValidationRegex.test(val) || val.length === 0, {
      message:
        "Invalid characters detected. Only letters, spaces, apostrophes ('), periods (.), and hyphens (-) are allowed.'",
    }),
  gender: z.string().min(3, {
    message:
      'The information is mandatory, please select one of the options above before continue.',
  }),
  occupation: z.string().min(1, {
    message: 'Please search and select your occupation.',
  }),
  industry: z.string().min(3, {
    message: 'Please select one of the options above before continue.',
  }),
  birthDate: z.string().date(),
});

export const AddressSchema = z.object({
  useAutoAddress: z.boolean(),
  suggestionSelected: z.boolean(),
  autocompleteAddress: z.string(),
  addressContainer: z.string().optional(),
  unit: z
    .string()
    .optional()
    .refine((val) => !val || forbiddenAddressRegex.test(val), {
      message:
        'Invalid characters detected. Please remove symbols like ~, ?, | , +, : , *, ^, <>=, (), [], {}, and \\',
    }),
  streetName: z
    .string()
    .min(1, { message: 'Please enter your street name' })
    .refine((val) => forbiddenAddressRegex.test(val), {
      message:
        'Invalid characters detected. Please remove symbols like ~, ?, | , +, : , *, ^, <>=, (), [], {}, and \\',
    }),
  buildingNumber: z
    .string()
    .min(1, { message: 'Please enter your building number' })
    .refine((val) => forbiddenAddressRegex.test(val), {
      message:
        'Invalid characters detected. Please remove symbols like ~, ?, | , +, : , *, ^, <>=, (), [], {}, and \\',
    }),
  postalCode: z
    .string()
    .min(1, { message: 'Please enter your postal code' })
    .refine((val) => postalCodeRegex.test(val), {
      message:
        'Invalid characters detected. The value must follow letter-number-letter pattern with a space in the middle and no invalid letters.',
    }),

  city: z
    .string()
    .min(1, { message: 'Please enter your city' })
    .refine((val) => forbiddenAddressRegex.test(val), {
      message:
        'Invalid characters detected. Please remove symbols like ~, ?, | , +, : , *, ^, <>=, (), [], {}, and \\',
    }),

  province: z.string().min(1, { message: 'Please enter your province' }),
  provinceCode: z
    .string()
    .min(1, { message: 'Please enter your province code' }),
  country: z.string().min(1),
  countryCode: z.string().length(2),
});

export const PhoneSchema = z.object;

export const RegistrationDataSchema = z
  .object({
    email: z.string().email({ message: 'Please enter a valid email address.' }),
    password: z
      .string()
      .min(8, { message: 'Password must be more than 8 characters' })
      .max(20, { message: 'Password must be less than 20 characters' }),
    promoId: z
      .number()
      .positive({ message: 'Please select an offer' })
      .or(z.literal(undefined)),
    address: AddressSchema,
    phone: z.object({
      phoneNumber: z
        .string()
        .min(1, { message: 'Please enter your phone number' }),
      phoneCode: z.string().min(1),
      phoneCountry: z.string().min(2).max(3),
    }),
    // marketingPreferences: MarketingPreferencesSchema,
    marketingMeta: MarketingMetaSchema,
    marketingMetaHistory: z.array(MarketingMetaSchema),
    limits: RegistrationLimitSchema,
    terms: z.boolean(),
    fit: z.boolean(),
    age: z.boolean(),
    pep: z.boolean(),
    marketing: z.boolean(),
  })
  .merge(PersonalDetailsSchema);

export const RefinedRegistrationDataSchema = RegistrationDataSchema.omit({
  marketingMeta: true,
  marketingMetaHistory: true,
})
  .refine(
    ({ phone }) =>
      isValidNumber(
        `${phone.phoneCode}${phone.phoneNumber}`,
        phone.phoneCountry as CountryCode,
      ),
    {
      message: 'Please enter a valid phone number',
      path: ['phone'],
    },
  )
  .refine(
    ({ birthDate }) => {
      if (!dayjs(birthDate, 'YYYY-MM-DD', true).isValid()) {
        return true;
      }
      return dayjs(birthDate).isBefore(dayjs().subtract(19, 'years'));
    },
    {
      message: 'You must be 19 years or older to continue',
      path: ['birthDate'],
    },
  )

  .refine(
    ({ birthDate }) => dayjs(birthDate).isAfter(dayjs().subtract(100, 'years')),
    {
      message:
        'Birth year can be max 100 years ago. If you believe this is incorrect, please contact Customer Support',
      path: ['birthDate'],
    },
  )
  .refine(({ terms }) => terms, {
    message: 'You must accept the terms',
    path: ['terms'],
  })
  .refine(({ age }) => age, {
    message: 'You must accept the terms',
    path: ['age'],
  })
  .refine(({ pep }) => pep, {
    message: 'You must accept the terms',
    path: ['pep'],
  })
  .refine(({ fit }) => fit, {
    message: 'You must accept the terms',
    path: ['fit'],
  });
